import * as React from 'react'
import { useState, ChangeEvent, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react'
import {
  FahrzeugKlasse,
  Antriebsart,
  Zahlungsintervall,
  type MVSParams,
  MVSErgebnis
} from '../../types/mvs'
import { berechneVergleich, type MVSVergleichsErgebnis } from '../../lib/mvs-vergleich-calculator'
import { SteuerVergleichTabelle } from './SteuerVergleichTabelle'
import { trackCalculation, trackFeedback, trackShare, trackFormField, trackPageView } from '../../lib/analytics'
import { berechneAlteSteuer } from '../../lib/mvs-alt-calculator'
import { berechneNeueSteuer } from '../../lib/mvs-neu-calculator'
import { incrementLikeCounter, getLikeCounter } from '../../lib/firebase-db'
import { HandThumbUpIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/solid'

// Lazy-loaded Komponenten für bessere Performance
const LazyFAQSection = lazy(() => import('./MVSCalculatorFAQ').then(module => ({ default: module.MVSCalculatorFAQ })))

// SEO Metadaten für die übergeordnete Komponente
export function getMVSRechnerMeta() {
  return {
    title: "MVS-Rechner Österreich | Vergleich alte und neue Regelung ab 04/2025",
    description: "Berechnen Sie die Motorfahrzeugsteuer in Österreich und vergleichen Sie die alte mit der neuen Regelung. Unterstützt alle Fahrzeugtypen und Antriebsarten.",
    keywords: "MVS-Rechner, Motorfahrzeugsteuer, Österreich, Versicherungssteuer, KFZ-Steuer, motorbezogene Versicherungssteuer, MVS, MVS Vergleich, Steuerrechner, Fahrzeugsteuer, CO2-Emission, Fahrzeugklasse, Antriebsart, Elektroauto, Plugin-Hybrid",
    canonicalUrl: "/mvs-rechner",
    openGraph: {
      title: "MVS-Rechner Vergleich (Alt vs. Neu ab 04/2025)",
      description: "Motorfahrzeugsteuer für Österreich berechnen und alte mit neuer Regelung vergleichen",
      image: "/images/mvs-rechner-og.jpg",
    }
  }
}

interface MVSCalculatorVergleichProps {
  onBack?: () => void
}

// Vordefinierte Optionen für Dropdown-Felder
const ZAHLUNGSINTERVALL_OPTIONEN = [
  { value: Zahlungsintervall.JAEHRLICH, label: 'Jährlich' },
  { value: Zahlungsintervall.HALBJAEHRLICH, label: 'Halbjährlich' },
  { value: Zahlungsintervall.VIERTELJAEHRLICH, label: 'Vierteljährlich' },
  { value: Zahlungsintervall.MONATLICH, label: 'Monatlich' }
]

const FAHRZEUGKLASSE_OPTIONEN = [
  { value: FahrzeugKlasse.M1_BIS_35T, label: 'PKW (M1) bis 3,5t' },
  { value: FahrzeugKlasse.L1E_BIS_L5E, label: 'Motorrad (L1e-L5e)' },
  { value: FahrzeugKlasse.WOHNMOBIL_M1_SA, label: 'Wohnmobile bis 3,5t' },
  { value: FahrzeugKlasse.LKW_N1_BIS_35T, label: 'LKW bis 3,5t (N1)' },
  { value: FahrzeugKlasse.ANDERE_BIS_35T, label: 'Sonstige KFZ bis 3,5t' }
]

// Dynamische Antriebsart-Optionen (fahrzeugKlasse optional, da initial ggf. undefined)
const getAntriebsartOptionen = (fahrzeugKlasse?: FahrzeugKlasse) => {
  const basisOptionen = [
    { value: Antriebsart.VERBRENNUNG, label: 'Verbrennung' },
    { value: Antriebsart.ELEKTRISCH, label: 'Elektrisch' },
    { value: Antriebsart.HYBRID_NICHT_EXTERN_AUFLADBAR, label: 'Hybridantrieb' }
  ]
  
  return basisOptionen
}

// Erweitern des MVSParams-Interface
type ExtendedMVSParams = Omit<MVSParams, 'fahrzeugKlasse' | 'antriebsart'> & {
  fahrzeugKlasse?: FahrzeugKlasse,
  antriebsart?: Antriebsart,
  istPluginHybrid: boolean,
  aufbauSA: boolean
}

// Standardparameter - mit undefinierter Fahrzeugklasse, damit richtig gefiltert wird
const DEFAULT_PARAMS: ExtendedMVSParams = {
  fahrzeugKlasse: undefined, // Standardmäßig undefiniert
  antriebsart: undefined, // Standardmäßig undefiniert
  erstzulassungsDatum: new Date(),
  motorLeistungKW: 100,
  hubraumCCM: 1800,
  co2EmissionGKM: 120,
  eigengewichtKG: 2200,
  zahlungsintervall: Zahlungsintervall.JAEHRLICH,
  istFremdzuendungsMotorVor1987: false,
  haeltSchadstoffgrenzwerteEin: true,
  istPluginHybrid: false,
  aufbauSA: false
}

// Struktur für Feldinformationen mit Tooltip-Text
interface FieldInfo {
  name: keyof ExtendedMVSParams
  label: string
  tooltipText: string
  type: 'select' | 'number' | 'date' | 'checkbox'
  options?: { value: string; label: string }[]
  condition?: (params: ExtendedMVSParams) => boolean
}

// Feldkonfiguration
const FIELD_CONFIG: FieldInfo[] = [
  {
    name: 'fahrzeugKlasse',
    label: 'Fahrzeugklasse',
    tooltipText: 'Art des Kraftfahrzeugs',
    type: 'select',
    options: FAHRZEUGKLASSE_OPTIONEN,
    condition: () => true // Immer anzeigen
  },
  {
    name: 'antriebsart',
    label: 'Antriebsart',
    tooltipText: 'Art des Antriebs des Fahrzeugs',
    type: 'select',
    options: getAntriebsartOptionen(),
    condition: (params) => params.fahrzeugKlasse !== undefined // Nur anzeigen, wenn Fahrzeugklasse ausgewählt
  },
  {
    name: 'istPluginHybrid',
    label: 'Plugin-Hybrid',
    tooltipText: 'Handelt es sich um einen extern aufladbaren Hybridantrieb (Plugin-Hybrid)?',
    type: 'checkbox',
    condition: (params) => params.fahrzeugKlasse !== undefined && 
                         params.antriebsart === Antriebsart.HYBRID_NICHT_EXTERN_AUFLADBAR
  },
  {
    name: 'aufbauSA',
    label: 'Mit Aufbau SA',
    tooltipText: 'Hat das Wohnmobil den Aufbau SA? (Basisfahrzeug ist ein Kraftfahrzeug der Klasse N)',
    type: 'checkbox',
    condition: (params) => params.fahrzeugKlasse === FahrzeugKlasse.WOHNMOBIL_M1_SA && 
                         params.antriebsart !== undefined
  },
  {
    name: 'erstzulassungsDatum',
    label: 'Erstzulassungsdatum',
    tooltipText: 'Datum der ersten Zulassung des Fahrzeugs (Feld B im Zulassungsschein)',
    type: 'date',
    condition: (params) => params.fahrzeugKlasse !== undefined && params.antriebsart !== undefined
  },
  {
    name: 'motorLeistungKW',
    label: 'Motorleistung (kW)',
    tooltipText: 'Leistung des Motors in Kilowatt laut Typenschein oder Zulassungsbescheinigung (Feld P2 im Zulassungsschein)',
    type: 'number',
    condition: (params) => {
      if (params.fahrzeugKlasse === undefined || params.antriebsart === undefined) return false
      
      // Motorräder mit Elektroantrieb oder alle anderen KFZ-Typen
      const istElektrischesMotorrad = params.fahrzeugKlasse === FahrzeugKlasse.L1E_BIS_L5E && 
                                     params.antriebsart === Antriebsart.ELEKTRISCH
      const keinMotorrad = params.fahrzeugKlasse !== FahrzeugKlasse.L1E_BIS_L5E
      
      return istElektrischesMotorrad || keinMotorrad
    }
  },
  {
    name: 'hubraumCCM',
    label: 'Hubraum (ccm)',
    tooltipText: 'Hubraum in Kubikzentimeter laut Typenschein oder Zulassungsbescheinigung (Feld P1 im Zulassungsschein)',
    type: 'number',
    condition: (params) => {
      if (params.fahrzeugKlasse === undefined || params.antriebsart === undefined) return false
      
      // Nur für Motorräder mit Verbrennungsmotor oder Hybrid
      const istMotorrad = params.fahrzeugKlasse === FahrzeugKlasse.L1E_BIS_L5E
      const hatVerbrennungsmotor = params.antriebsart === Antriebsart.VERBRENNUNG || 
                                  params.antriebsart === Antriebsart.HYBRID_NICHT_EXTERN_AUFLADBAR
      return istMotorrad && hatVerbrennungsmotor
    }
  },
  {
    name: 'co2EmissionGKM',
    label: 'CO₂-Emission (g/km)',
    tooltipText: 'CO₂-Emission in Gramm pro Kilometer laut Typenschein oder Zulassungsbescheinigung (Feld V.7 oder Feld A24 im Zulassungsschein)',
    type: 'number',
    condition: (params) => {
      if (params.fahrzeugKlasse === undefined || 
          params.antriebsart === undefined || 
          params.erstzulassungsDatum === undefined) return false
      
      const istNachStichtag = params.erstzulassungsDatum >= new Date('2020-10-01')
      return istNachStichtag
    }
  },
  {
    name: 'eigengewichtKG',
    label: 'Eigengewicht (kg)',
    tooltipText: 'Eigengewicht in Kilogramm laut Typenschein oder Zulassungsbescheinigung (Feld G im Zulassungsschein)',
    type: 'number',
    condition: (params) => {
      if (params.fahrzeugKlasse === undefined || params.antriebsart === undefined) return false
      
      // Nur für Elektrofahrzeuge
      return params.antriebsart === Antriebsart.ELEKTRISCH
    }
  },
  {
    name: 'zahlungsintervall',
    label: 'Zahlungsweise',
    tooltipText: 'Gewünschtes Intervall für die Steuerzahlung. Bei kürzeren Intervallen fällt ein Zuschlag an',
    type: 'select',
    options: ZAHLUNGSINTERVALL_OPTIONEN,
    condition: (params) => params.fahrzeugKlasse !== undefined && params.antriebsart !== undefined
  },
  {
    name: 'istFremdzuendungsMotorVor1987',
    label: 'Fremdzündungsmotor vor 1987',
    tooltipText: 'Fremdzündungsmotor, dessen Typengenehmigung vor dem 1. Oktober 1987 erfolgt ist',
    type: 'checkbox',
    condition: (params) => {
      if (params.fahrzeugKlasse === undefined || params.antriebsart === undefined) return false
      
      return params.antriebsart === Antriebsart.VERBRENNUNG || 
            (params.antriebsart === Antriebsart.HYBRID_NICHT_EXTERN_AUFLADBAR && !params.istPluginHybrid)
    }
  },
  {
    name: 'haeltSchadstoffgrenzwerteEin',
    label: 'Hält Schadstoffgrenzwerte ein',
    tooltipText: 'Fahrzeug hält die Schadstoffgrenzwerte gemäß EG-Richtlinie 83/351 ein',
    type: 'checkbox',
    condition: (params) => params.fahrzeugKlasse !== undefined && 
                         params.antriebsart !== undefined && 
                         params.istFremdzuendungsMotorVor1987
  }
]

// Hilfsfunktion für die Formularfelder mit Tooltip
function FormField({ 
  label, 
  children, 
  tooltip 
}: { 
  label: string, 
  children: React.ReactNode, 
  tooltip: string 
}) {
  return (
    <div className="mb-3">
      <div className="flex items-center mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="relative ml-2 group">
          <div className="bg-orange-500 text-white rounded-full w-4 h-4 flex items-center justify-center cursor-help text-xs">
            i
          </div>
          <div className="absolute z-10 w-64 p-2 bg-white border border-orange-200 rounded-lg shadow-lg 
                          opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity 
                          left-full -translate-x-2 top-0 text-xs text-gray-700">
            {tooltip}
          </div>
        </div>
      </div>
      {children}
    </div>
  )
}

// Formatierung von Währungsbeträgen
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

// Hilfsfunktion zur Anzeige des Zahlungsintervalls als Text
function getZahlungsintervallText(intervall: Zahlungsintervall): string {
  switch (intervall) {
    case Zahlungsintervall.JAEHRLICH:
      return "Jahr"
    case Zahlungsintervall.HALBJAEHRLICH:
      return "Halbjahr"
    case Zahlungsintervall.VIERTELJAEHRLICH:
      return "Quartal"
    case Zahlungsintervall.MONATLICH:
      return "Monat"
  }
}

// Hauptkomponente für den MVS-Rechner mit Vergleich
export function MVSCalculatorVergleich({ onBack }: MVSCalculatorVergleichProps) {
  const [params, setParams] = useState<ExtendedMVSParams>(DEFAULT_PARAMS)
  const [vergleichsErgebnis, setVergleichsErgebnis] = useState<MVSVergleichsErgebnis | null>(null)
  const [antriebsartOptionen, setAntriebsartOptionen] = useState(
    params.fahrzeugKlasse !== undefined
      ? getAntriebsartOptionen(params.fahrzeugKlasse)
      : getAntriebsartOptionen()
  )
  // Speichere den vorherigen CO2-Wert, falls der Benutzer zu Elektro wechselt und wieder zurück
  const [vorherigeCO2Emission, setVorherigeCO2Emission] = useState<number | undefined>(DEFAULT_PARAMS.co2EmissionGKM)
  // Eindeutige ID für diese Komponenteninstanz
  const instanceId = useRef(`mvs_vergleich_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`).current
  // Feedback-Status
  const [feedbackGegeben, setFeedbackGegeben] = useState<boolean>(false)
  const [likeCount, setLikeCount] = useState<number>(0) // Startwert auf 0
  const [showFAQ, setShowFAQ] = useState<boolean>(false)
  // Berechnungs-ID für Sharing und Speicherung in Firebase
  const [calculationId, setCalculationId] = useState<string | null>(null)
  // Ref für das Teilen von Ergebnissen
  const shareUrlRef = useRef<HTMLInputElement>(null)
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  // Puffer für Datumseingabe (TTMMJJJJ nur Ziffern)
  const [erstzulassungInput, setErstzulassungInput] = useState<string>('')
  const erstzulassungDebounceRef = useRef<any>(null)
  function formatDateToISO(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  useEffect(() => {
    const d = params.erstzulassungsDatum
    if (d instanceof Date && !isNaN(d.getTime())) {
      const iso = formatDateToISO(d)
      if (iso !== erstzulassungInput) setErstzulassungInput(iso)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    const d = params.erstzulassungsDatum
    if (d instanceof Date && !isNaN(d.getTime())) {
      const iso = formatDateToISO(d)
      if (iso !== erstzulassungInput) setErstzulassungInput(iso)
    }
  }, [params.erstzulassungsDatum])

  // Lade den aktuellen Like-Zähler aus der Datenbank beim Laden der Komponente
  useEffect(() => {
    async function loadLikeCounter() {
      try {
        // Prüfen, ob der Benutzer zugestimmt hat
        const consentStatus = localStorage.getItem('analytics-consent')
        if (consentStatus === 'true') {
          const result = await getLikeCounter()
          if (result.success) {
            // Stelle sicher, dass wir einen gültigen Zahlenwert haben
            const count = typeof result.count === 'number' ? result.count : 0
            setLikeCount(count)
          }
        } else {
          // Wenn keine Zustimmung vorhanden ist, starten wir mit dem Standardwert
          console.log("Keine Cookie-Zustimmung vorhanden, lokaler Zählerstand wird verwendet")
        }
      } catch (error) {
        console.error("Fehler beim Laden des Like-Zählers:", error)
      }
    }
    
    loadLikeCounter()
  }, [])

  // Berechnung bei Änderung der Parameter - mit memoization
  useEffect(() => {
    // Nur berechnen, wenn die notwendigen Parameter gesetzt sind
    if (params.fahrzeugKlasse === undefined || params.antriebsart === undefined) {
      setVergleichsErgebnis(null)
      return
    }
    
    // Konvertiere ExtendedMVSParams zu MVSParams für die Berechnung
    const berechnungsParams: MVSParams = { 
      fahrzeugKlasse: params.fahrzeugKlasse,
      antriebsart: params.antriebsart,
      erstzulassungsDatum: params.erstzulassungsDatum,
      motorLeistungKW: params.motorLeistungKW,
      hubraumCCM: params.hubraumCCM,
      co2EmissionGKM: params.co2EmissionGKM,
      eigengewichtKG: params.eigengewichtKG,
      zahlungsintervall: params.zahlungsintervall,
      istFremdzuendungsMotorVor1987: params.istFremdzuendungsMotorVor1987,
      haeltSchadstoffgrenzwerteEin: params.haeltSchadstoffgrenzwerteEin
    }
    
    // Bei Wohnmobilen MIT Aufbau SA als ANDERE_BIS_35T rechnen, bei Wohnmobilen OHNE Aufbau SA als M1_BIS_35T (PKW) rechnen
    if (params.fahrzeugKlasse === FahrzeugKlasse.WOHNMOBIL_M1_SA) {
      if (params.aufbauSA) {
        // Mit Aufbau SA: Wie andere KFZ berechnen (mit Obergrenze)
        berechnungsParams.fahrzeugKlasse = FahrzeugKlasse.ANDERE_BIS_35T;
      } else {
        // Ohne Aufbau SA: Wie PKW berechnen (ohne Obergrenze)
        berechnungsParams.fahrzeugKlasse = FahrzeugKlasse.M1_BIS_35T;
      }
    }
    
    // Bei Plugin-Hybrid die korrekte Antriebsart setzen
    if (params.antriebsart === Antriebsart.HYBRID_NICHT_EXTERN_AUFLADBAR && params.istPluginHybrid) {
      berechnungsParams.antriebsart = Antriebsart.HYBRID_EXTERN_AUFLADBAR
    }
    
    berechneErgebnis(berechnungsParams)
  }, [params])

  // Aktualisiere die Antriebsart-Optionen, wenn sich die Fahrzeugklasse ändert
  useEffect(() => {
    if (params.fahrzeugKlasse !== undefined) {
      setAntriebsartOptionen(getAntriebsartOptionen(params.fahrzeugKlasse))
    } else {
      setAntriebsartOptionen(getAntriebsartOptionen())
    }
  }, [params.fahrzeugKlasse])

  // Behandlung von Änderungen bei Formularfeldern
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    
    // Werte entsprechend ihrem Typ konvertieren
    if (type === 'number') {
      setParams(prev => ({ ...prev, [name]: value === '' ? undefined : Number(value) }))
    } else if (type === 'checkbox') {
      setParams(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }))
    } else if (name === 'erstzulassungsDatum') {
      // Spezielle Logik im Feld-spezifischen Handler; hier nichts tun
      return
    } else if (name === 'fahrzeugKlasse') {
      // Wenn Fahrzeugklasse geändert wird, setze Antriebsart zurück
      setParams(prev => ({ 
        ...prev, 
        [name]: value as FahrzeugKlasse,
        antriebsart: undefined // Zurücksetzen auf undefined
      }))
    } else if (name === 'antriebsart') {
      const neueAntriebsart = value as Antriebsart
      
      // Wenn der neue Wert Elektrisch ist, speichere den aktuellen CO2-Wert und setze auf 0
      if (neueAntriebsart === Antriebsart.ELEKTRISCH) {
        setVorherigeCO2Emission(params.co2EmissionGKM)
        setParams(prev => ({ 
          ...prev, 
          [name]: neueAntriebsart,
          co2EmissionGKM: 0,
          istPluginHybrid: false
        }))
      } 
      // Wenn der vorherige Wert Elektrisch war und wir wechseln, stelle den gespeicherten CO2-Wert wieder her
      else if (params.antriebsart === Antriebsart.ELEKTRISCH) {
        setParams(prev => ({ 
          ...prev, 
          [name]: neueAntriebsart,
          co2EmissionGKM: vorherigeCO2Emission,
          istPluginHybrid: false
        }))
      }
      // Sonst normales Update
      else {
        setParams(prev => ({ 
          ...prev, 
          [name]: neueAntriebsart,
          istPluginHybrid: false
        }))
      }
    } else {
      setParams(prev => ({ ...prev, [name]: value }))
    }
    
    // Analytics-Tracking für wichtige Felder
    if (['fahrzeugKlasse', 'antriebsart', 'co2EmissionGKM', 'motorLeistungKW'].includes(name)) {
      const numericTracked = ['co2EmissionGKM', 'motorLeistungKW']
      const trackedValue = numericTracked.includes(name) ? Number(value) : value
      trackFormField(name, trackedValue as any)
    }
  }, [params.antriebsart, params.co2EmissionGKM, vorherigeCO2Emission]);

  // Berechnung des Ergebnisses
  const berechneErgebnis = useCallback((berechnungsParams: MVSParams) => {
    // Berechnung mit dem Vergleichsrechner
    const ergebnis = berechneVergleich(berechnungsParams)
    setVergleichsErgebnis(ergebnis)
    
    // Analytics-Tracking der Berechnung
    trackCalculation(
      'vergleich', 
      berechnungsParams.fahrzeugKlasse
    )
  }, [])

  // Renderfunktion für dynamische Formularfelder - memoization
  const renderField = useCallback((fieldInfo: FieldInfo) => {
    // Prüfen, ob das Feld angezeigt werden soll
    if (fieldInfo.condition && !fieldInfo.condition(params)) {
      return null
    }

    // Eindeutige ID für dieses Feld erzeugen
    const fieldId = `${instanceId}_${fieldInfo.name}`

    // Feldinhalt basierend auf Typ rendern
    let fieldContent = null
    
    switch (fieldInfo.type) {
      case 'select':
        // Behandle Select-Felder sicherer mit Typzusicherung
        const selectValue = params[fieldInfo.name] !== undefined 
          ? params[fieldInfo.name] as string 
          : '';
          
        fieldContent = (
          <div className="relative">
            <select
              name={fieldInfo.name}
              id={fieldId}
              aria-label={fieldInfo.label}
              value={selectValue}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md bg-white shadow-sm 
                      focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 
                      appearance-none text-gray-800"
            >
              <option value="">Bitte auswählen</option>
              {fieldInfo.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-orange-600">
              <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        )
        break
      
      case 'number':
        // Sicherer Umgang mit number-Feldern
        const numberValue = typeof params[fieldInfo.name] === 'number' 
          ? params[fieldInfo.name] as number 
          : '';
          
        fieldContent = (
          <input
            type="number"
            name={fieldInfo.name}
            id={fieldId}
            aria-label={fieldInfo.label}
            value={numberValue}
            onChange={handleChange}
            min="0"
            className="w-full p-2 border border-gray-300 rounded-md bg-white shadow-sm 
                     focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 
                     text-gray-800"
          />
        )
        break
      
      case 'date':
        if (fieldInfo.name === 'erstzulassungsDatum') {
          fieldContent = (
            <input
              type="date"
              name={fieldInfo.name}
              id={fieldId}
              aria-label={fieldInfo.label}
              defaultValue={(() => {
                const d: any = params[fieldInfo.name]
                if (d instanceof Date && !isNaN(d.getTime())) {
                  const y = d.getFullYear()
                  const m = String(d.getMonth() + 1).padStart(2, '0')
                  const day = String(d.getDate()).padStart(2, '0')
                  return `${y}-${m}-${day}`
                }
                return ''
              })()}
              onChange={(e) => {
                const next = (e.target as HTMLInputElement).value
                if (erstzulassungDebounceRef?.current) {
                  clearTimeout(erstzulassungDebounceRef.current as any)
                }
                erstzulassungDebounceRef.current = setTimeout(() => {
                  if (next && next.length === 10) {
                    const parts = next.split('-').map(Number)
                    if (parts.length === 3) {
                      const [y, m, d] = parts
                      const local = new Date(y, m - 1, d)
                      if (!isNaN(local.getTime()) && local.getFullYear() === y && local.getMonth() === m - 1 && local.getDate() === d) {
                        setParams(prev => ({ ...prev, erstzulassungsDatum: local }))
                      }
                    }
                  }
                }, 1000)
              }}
              onBlur={() => {
                const s = (event?.target as HTMLInputElement | undefined)?.value || ''
                if (s && s.length === 10) {
                  const parts = s.split('-').map(Number)
                  if (parts.length === 3) {
                    const [y, m, d] = parts
                    const local = new Date(y, m - 1, d)
                    if (!isNaN(local.getTime()) && local.getFullYear() === y && local.getMonth() === m - 1 && local.getDate() === d) {
                      setParams(prev => ({ ...prev, erstzulassungsDatum: local }))
                    }
                  }
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const s = (e.target as HTMLInputElement).value
                  if (s && s.length === 10) {
                    const parts = s.split('-').map(Number)
                    if (parts.length === 3) {
                      const [y, m, d] = parts
                      const local = new Date(y, m - 1, d)
                      if (!isNaN(local.getTime()) && local.getFullYear() === y && local.getMonth() === m - 1 && local.getDate() === d) {
                        setParams(prev => ({ ...prev, erstzulassungsDatum: local }))
                      }
                    }
                  }
                }
              }}
              className="w-full p-2 border border-gray-300 rounded-md bg-white shadow-sm 
                       focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 
                       text-gray-800 tracking-wider"
            />
          )
          break
        }
        let dateValue = ''
        const dateParam: any = params[fieldInfo.name]
        if (dateParam instanceof Date) {
          try {
            // Stellen Sie sicher, dass das Datum gültig ist
            if (!isNaN(dateParam.getTime())) {
              const year = dateParam.getFullYear()
              const month = String(dateParam.getMonth() + 1).padStart(2, '0')
              const day = String(dateParam.getDate()).padStart(2, '0')
              dateValue = `${year}-${month}-${day}`
            }
          } catch (error) {
            console.warn('Ungültiges Datum:', error)
          }
        }
          
        fieldContent = (
          <input
            type="date"
            name={fieldInfo.name}
            id={fieldId}
            aria-label={fieldInfo.label}
            value={dateValue}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md bg-white shadow-sm 
                     focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 
                     text-gray-800"
          />
        )
        break
      
      case 'checkbox':
        // Sichere Typzuweisung für Checkboxes
        const isChecked = typeof params[fieldInfo.name] === 'boolean' 
          ? params[fieldInfo.name] as boolean
          : false;
          
        fieldContent = (
          <div className="flex items-center">
            <input
              type="checkbox"
              name={fieldInfo.name}
              id={fieldId}
              aria-label={fieldInfo.label}
              checked={isChecked}
              onChange={handleChange}
              className="h-4 w-4 text-orange-600 border-gray-300 rounded shadow-sm 
                        focus:ring-1 focus:ring-orange-500"
            />
            <label htmlFor={fieldId} className="ml-2 text-sm font-medium text-gray-700">
              {fieldInfo.label}
            </label>
          </div>
        )
        break
    }

    return fieldInfo.type === 'checkbox' ? (
      <div className="mb-3" key={fieldInfo.name}>
        <div className="relative group">
          {fieldContent}
          <div className="absolute top-0 left-8 z-10 w-60 p-2 bg-white border border-orange-200 rounded-lg shadow-lg 
                          opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity 
                          text-xs text-gray-700 mt-6">
            {fieldInfo.tooltipText}
          </div>
        </div>
      </div>
    ) : (
      <FormField 
        key={fieldInfo.name} 
        label={fieldInfo.label} 
        tooltip={fieldInfo.tooltipText}
      >
        {fieldContent}
      </FormField>
    )
  }, [params, instanceId, handleChange]);

  // Memoize die Formularfelder für bessere Performance
  const formularFelder = useMemo(() => {
    return FIELD_CONFIG.map(fieldInfo => renderField(fieldInfo));
  }, [renderField]);

  // Die Vergleichskomponente nur neu rendern wenn sich die Daten ändern
  const vergleichsTabelle = useMemo(() => {
    if (!vergleichsErgebnis) return null;
    
    // Stichtag für Zuschlagsregelung
    const stichtag = new Date(2020, 9, 1) // Monat ist 0-basiert, daher 9 für Oktober
    const istNachStichtag = params.erstzulassungsDatum >= stichtag
    
    // Gemäß Gesetz: Bei Erstzulassung nach 30.09.2020 keine Zuschläge mehr
    const sindZuschlaegeFuerZahlungsintervallAnwendbar = !istNachStichtag
    
    return (
      <SteuerVergleichTabelle 
        steuerAlt={vergleichsErgebnis.altesSystem}
        steuerNeu={vergleichsErgebnis.neuesSystem}
        sindZuschlaegeFuerZahlungsintervallAnwendbar={sindZuschlaegeFuerZahlungsintervallAnwendbar}
        className="text-gray-800 text-sm"
      />
    );
  }, [vergleichsErgebnis, params.erstzulassungsDatum]);

  // Generiere URL basierend auf aktuellen Parametern und Ergebnis
  const generateShareUrl = useCallback(() => {
    if (!vergleichsErgebnis) return "";
    
    const baseUrl = window.location.origin + window.location.pathname;
    
    // Parameter-Objekt mit relevanten Werten erstellen
    const urlParams = new URLSearchParams();
    urlParams.set("fzk", params.fahrzeugKlasse?.toString() || "");
    urlParams.set("ant", params.antriebsart?.toString() || "");
    urlParams.set("kw", params.motorLeistungKW?.toString() || "");
    urlParams.set("co2", params.co2EmissionGKM?.toString() || "");
    
    if (params.hubraumCCM) urlParams.set("ccm", params.hubraumCCM.toString());
    if (params.eigengewichtKG) urlParams.set("kg", params.eigengewichtKG.toString());
    
    // Zahlungsintervall hinzufügen
    urlParams.set("zi", params.zahlungsintervall.toString());
    
    // Datum formatieren
    let erstzulassung = ''
    const erstzulassungsDatum = params.erstzulassungsDatum
    if (erstzulassungsDatum instanceof Date) {
      try {
        if (!isNaN(erstzulassungsDatum.getTime())) {
          const year = erstzulassungsDatum.getFullYear()
          const month = String(erstzulassungsDatum.getMonth() + 1).padStart(2, '0')
          const day = String(erstzulassungsDatum.getDate()).padStart(2, '0')
          erstzulassung = `${year}-${month}-${day}`
        }
      } catch (error) {
        console.warn('Fehler bei der Datumsformatierung:', error)
      }
    }
    if (erstzulassung) urlParams.set("date", erstzulassung)
    
    // Checkboxen als 0/1 codieren
    if (params.istPluginHybrid) urlParams.set("plugin", "1");
    if (params.aufbauSA) urlParams.set("sa", "1");
    if (params.istFremdzuendungsMotorVor1987) urlParams.set("fzm", "1");
    if (params.haeltSchadstoffgrenzwerteEin) urlParams.set("sgw", "1");
    
    return `${baseUrl}?${urlParams.toString()}`;
  }, [params, vergleichsErgebnis]);

  // Verarbeite URL-Parameter beim Laden
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      
      // Objekt zum Sammeln der Parameter
      const urlParamValues: Partial<ExtendedMVSParams> = {};
      
      // Parameter auslesen
      const fzk = urlParams.get("fzk");
      if (fzk) urlParamValues.fahrzeugKlasse = fzk as FahrzeugKlasse;
      
      const ant = urlParams.get("ant");
      if (ant) urlParamValues.antriebsart = ant as Antriebsart;
      
      const kw = urlParams.get("kw");
      if (kw) urlParamValues.motorLeistungKW = parseInt(kw, 10);
      
      const co2 = urlParams.get("co2");
      if (co2) urlParamValues.co2EmissionGKM = parseInt(co2, 10);
      
      const ccm = urlParams.get("ccm");
      if (ccm) urlParamValues.hubraumCCM = parseInt(ccm, 10);
      
      const kg = urlParams.get("kg");
      if (kg) urlParamValues.eigengewichtKG = parseInt(kg, 10);
      
      const zi = urlParams.get("zi");
      if (zi) urlParamValues.zahlungsintervall = zi as Zahlungsintervall;
      
      const date = urlParams.get("date");
      if (date) urlParamValues.erstzulassungsDatum = new Date(date);
      
      // Checkboxen
      urlParamValues.istPluginHybrid = urlParams.get("plugin") === "1";
      urlParamValues.aufbauSA = urlParams.get("sa") === "1";
      urlParamValues.istFremdzuendungsMotorVor1987 = urlParams.get("fzm") === "1";
      urlParamValues.haeltSchadstoffgrenzwerteEin = urlParams.get("sgw") === "1";
      
      // Nur setzen wenn relevante Parameter vorhanden sind
      if (Object.keys(urlParamValues).length > 0 && urlParamValues.fahrzeugKlasse && urlParamValues.antriebsart) {
        setParams(prevParams => ({
          ...prevParams,
          ...urlParamValues,
          zahlungsintervall: urlParamValues.zahlungsintervall || Zahlungsintervall.JAEHRLICH // Default-Wert
        }));
      }
    } catch (error) {
      console.error("Fehler beim Parsen der URL-Parameter:", error);
    }
  }, []);
  
  // Kopiere die Share-URL in die Zwischenablage
  const copyShareUrl = useCallback(async () => {
    const shareUrl = generateShareUrl();
    if (!shareUrl) return;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      if (shareUrlRef.current) {
        shareUrlRef.current.select();
        shareUrlRef.current.setSelectionRange(0, 99999);
      }
    } catch (err) {
      console.error("Fehler beim Kopieren:", err);
    }
  }, [generateShareUrl]);

  // Feedback geben (Daumen hoch)
  const gebeFeedback = useCallback(() => {
    if (!feedbackGegeben) {
      setFeedbackGegeben(true)
      
      // Analytics-Tracking des Feedbacks
      trackFeedback(true)
      
      // Like-Zähler in Firebase inkrementieren
      const consentStatus = localStorage.getItem('analytics-consent')
      if (consentStatus === 'true') {
        try {
          incrementLikeCounter()
            .then(result => {
              if (result.success) {
                // Aktualisiere den Zähler mit dem Wert aus der Datenbank
                const count = typeof result.count === 'number' ? result.count : 0
                setLikeCount(count)
                console.log("Like-Zähler erfolgreich inkrementiert. Neuer Wert:", count)
              }
            })
            .catch(error => {
              console.error("Fehler beim Inkrementieren des Like-Zählers:", error)
            })
        } catch (error) {
          console.error("Fehler beim Inkrementieren des Like-Zählers:", error)
        }
      } else {
        // Auch ohne Consent lokal den Zähler erhöhen, aber nicht in Firebase speichern
        setLikeCount(prev => prev + 1)
      }
    }
  }, [feedbackGegeben])

  // Social Media Sharing Funktionen
  const shareOnWhatsApp = useCallback(() => {
    const shareUrl = encodeURIComponent(generateShareUrl());
    const shareText = encodeURIComponent("MVS-Rechner Ergebnis: Vergleich der Motorfahrzeugsteuer in Österreich");
    window.open(`https://wa.me/?text=${shareText}%20${shareUrl}`, '_blank');
    
    // Analytics-Tracking des Sharings
    trackShare('WhatsApp');
  }, [generateShareUrl]);

  const shareOnFacebook = useCallback(() => {
    const shareUrl = encodeURIComponent(generateShareUrl());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, '_blank');
    
    // Analytics-Tracking des Sharings
    trackShare('Facebook');
  }, [generateShareUrl]);

  const shareOnTwitter = useCallback(() => {
    const shareUrl = encodeURIComponent(generateShareUrl());
    const shareText = encodeURIComponent("MVS-Rechner Vergleich: Alte vs. neue Regelung ab 04/2025");
    window.open(`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`, '_blank');
    
    // Analytics-Tracking des Sharings
    trackShare('Twitter');
  }, [generateShareUrl]);

  const shareByEmail = useCallback(() => {
    const shareUrl = generateShareUrl();
    const subject = encodeURIComponent("MVS-Rechner Ergebnis");
    const body = encodeURIComponent(`Schau dir mein Ergebnis im MVS-Rechner an: ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    
    // Analytics-Tracking des Sharings
    trackShare('Email');
  }, [generateShareUrl]);

  return (
    <section 
      className="bg-white p-4 shadow-md rounded-lg max-w-6xl mx-auto" 
      aria-labelledby="mvs-rechner-heading"
    >
      <h2 
        id="mvs-rechner-heading" 
        className="text-xl font-bold mb-4 text-orange-800 border-b border-orange-100 pb-2"
      >
        MVS-Rechner Vergleich <span className="font-normal text-base text-orange-600">(Alt vs. Neu ab 04/2025)</span>
      </h2>
      
      {/* Einleitungstext - visible for users and SEO */}
      <div className="mb-6 text-sm text-gray-700 bg-orange-50 p-3 rounded-md border border-orange-100">
        <p className="mb-2">
          Mit diesem Rechner können Sie die Motorfahrzeugsteuer (MVS) für Kraftfahrzeuge in Österreich berechnen und die Unterschiede zwischen der aktuellen und der neuen Regelung ab April 2025 vergleichen.
        </p>
        <p>
          Die <strong>motorbezogene Versicherungssteuer (MVS)</strong> wird im Rahmen der KFZ-Versicherung eingehoben. Die Berechnung erfolgt anhand verschiedener Fahrzeugdaten wie Leistung, Hubraum, Fahrzeugklasse und CO₂-Emissionen. Geben Sie die entsprechenden Werte aus Ihrem Zulassungsschein (Typenschein) ein, um Ihre individuelle Steuerbelastung zu berechnen.
        </p>
      </div>
      
      {onBack && (
        <button 
          onClick={onBack}
          className="mb-4 px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-md text-sm transition-colors flex items-center"
          aria-label="Zurück zur vorherigen Seite"
        >
          <svg className="w-3.5 h-3.5 mr-1" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path d="M15 19l-7-7 7-7"></path></svg>
          Zurück
        </button>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Eingabeformular */}
        <section 
          className="bg-gradient-to-b from-orange-50 to-white p-3 rounded-lg border border-orange-100 shadow-sm md:col-span-2"
          aria-labelledby="fahrzeugdaten-heading"
          role="region"
          aria-label="Fahrzeugdaten"
        >
          <h3 
            id="fahrzeugdaten-heading" 
            className="text-base font-semibold mb-3 text-orange-700 flex items-center"
          >
            <svg className="w-4 h-4 mr-1.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            Fahrzeugdaten
          </h3>
          
          <div className="space-y-3">
            {/* Dynamische Formularfelder - Memoized */}
            {formularFelder}
          </div>
        </section>
        
        {/* Ergebnistabelle und Zusammenfassung */}
        <section 
          className="bg-gradient-to-b from-orange-50 to-white p-3 rounded-lg border border-orange-100 shadow-sm md:col-span-3"
          aria-labelledby={vergleichsErgebnis ? "ergebnis-heading" : ""}
        >
          {/* Zusammenfassung */}
          {vergleichsErgebnis ? (
            <div>
              <div className="mb-4 p-3 bg-white border border-orange-200 rounded-lg shadow-sm">
                <h3 
                  id="ergebnis-heading" 
                  className="font-semibold text-base mb-3 text-orange-800 flex items-center"
                >
                  <svg className="w-4 h-4 mr-1.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                  Zusammenfassung
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-2 rounded-md">
                    <p className="text-xs font-medium mb-1 text-gray-600">Alte Regelung:</p>
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(vergleichsErgebnis.altesSystem.steuerJaehrlich)}</p>
                    <p className="text-xs text-gray-500">pro Jahr</p>
                  </div>
                  
                  <div className={`p-2 rounded-md ${
                    vergleichsErgebnis.differenz.jaehrlich > 0 
                      ? 'bg-red-50' 
                      : vergleichsErgebnis.differenz.jaehrlich < 0
                        ? 'bg-green-50'
                        : 'bg-green-50'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${
                      vergleichsErgebnis.differenz.jaehrlich > 0 
                        ? 'text-red-700' 
                        : vergleichsErgebnis.differenz.jaehrlich < 0
                          ? 'text-green-700'
                          : 'text-green-700'
                    }`}>Neue Regelung:</p>
                    <p className={`text-lg font-bold ${
                      vergleichsErgebnis.differenz.jaehrlich > 0 
                        ? 'text-red-600' 
                        : vergleichsErgebnis.differenz.jaehrlich < 0
                          ? 'text-green-600'
                          : 'text-green-600'
                    }`}>
                      {formatCurrency(vergleichsErgebnis.neuesSystem.steuerJaehrlich)}
                    </p>
                    <p className={`text-xs ${
                      vergleichsErgebnis.differenz.jaehrlich > 0 
                        ? 'text-red-500' 
                        : vergleichsErgebnis.differenz.jaehrlich < 0
                          ? 'text-green-500'
                          : 'text-green-500'
                    }`}>pro Jahr</p>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-orange-100">
                  <div className="flex items-baseline justify-between">
                    <span className="font-medium text-sm text-gray-700">Differenz:</span>
                    <div className="text-right">
                      <span className={`font-bold text-base ${vergleichsErgebnis.differenz.jaehrlich > 0 ? 'text-red-600' : vergleichsErgebnis.differenz.jaehrlich < 0 ? 'text-green-600' : 'text-green-600'}`}>
                        {formatCurrency(vergleichsErgebnis.differenz.jaehrlich)}
                      </span>
                      <span className={`ml-1 text-xs ${vergleichsErgebnis.differenz.jaehrlich > 0 ? 'text-red-600' : vergleichsErgebnis.differenz.jaehrlich < 0 ? 'text-green-600' : 'text-green-600'}`}>
                        ({vergleichsErgebnis.differenz.jaehrlich > 0 ? '+' : ''}{vergleichsErgebnis.differenz.jaehrlichProzent.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                  
                  <p className={`mt-2 text-xs px-2 py-1 rounded-md ${
                    vergleichsErgebnis.differenz.jaehrlich > 0 
                      ? 'text-red-800 bg-red-50' 
                      : vergleichsErgebnis.differenz.jaehrlich < 0 
                        ? 'text-green-800 bg-green-50' 
                        : 'text-green-800 bg-green-50'
                  }`}>
                    {vergleichsErgebnis.differenz.jaehrlich > 0 && 'Die Steuer wird mit der neuen Regelung höher sein.'}
                    {vergleichsErgebnis.differenz.jaehrlich < 0 && 'Die Steuer wird mit der neuen Regelung niedriger sein.'}
                    {vergleichsErgebnis.differenz.jaehrlich === 0 && 'Die Steuer bleibt mit der neuen Regelung gleich.'}
                  </p>
                </div>
                
                {/* Detaillierte Vergleichstabelle */}
                <div className="mt-4">
                  <h3 className="font-semibold text-base mb-3 text-orange-800 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
                    Detaillierter Vergleich
                  </h3>
                  {vergleichsTabelle}
                </div>
                
                {/* Feedback-Bereich mit Like-Button - Verschoben unter die Tabelle */}
                <div className="mt-4 pt-3 border-t border-orange-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">War dieser Rechner hilfreich?</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={gebeFeedback}
                        disabled={feedbackGegeben}
                        className={`flex items-center p-1.5 rounded-full transition-colors ${
                          feedbackGegeben
                            ? 'text-green-600 cursor-default' 
                            : 'text-gray-500 hover:text-orange-500'
                        }`}
                        aria-label="Daumen hoch für den MVS-Rechner"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                        </svg>
                      </button>
                      <span className="text-sm font-medium bg-gray-100 px-3 py-1.5 rounded-md border border-gray-200">
                        {likeCount} Bewertungen
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Ergebnis teilen - Verschoben unter die Tabelle */}
                <div className="mt-4 pt-3 border-t border-orange-100">
                  <div className="flex flex-col gap-2">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center">
                      <svg className="w-3.5 h-3.5 mr-1 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
                      </svg>
                      Berechnung teilen
                    </h4>
                    
                    {/* Social Media Sharing Buttons mit Kopieren-Button */}
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        onClick={copyShareUrl}
                        className="p-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs flex items-center"
                        aria-label="Link in die Zwischenablage kopieren"
                      >
                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
                        </svg>
                        Link kopieren
                      </button>
                      <button
                        onClick={shareOnWhatsApp}
                        className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded text-xs flex items-center"
                        aria-label="Auf WhatsApp teilen"
                      >
                        WhatsApp
                      </button>
                      <button
                        onClick={shareOnFacebook}
                        className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs flex items-center"
                        aria-label="Auf Facebook teilen"
                      >
                        Facebook
                      </button>
                      <button
                        onClick={shareOnTwitter}
                        className="p-1.5 bg-sky-500 hover:bg-sky-600 text-white rounded text-xs flex items-center"
                        aria-label="Auf Twitter teilen"
                      >
                        Twitter
                      </button>
                      <button
                        onClick={shareByEmail}
                        className="p-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs flex items-center"
                        aria-label="Per E-Mail teilen"
                      >
                        E-Mail
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Placeholder für das Ergebnis, wenn es noch nicht berechnet wurde */}
              <p>Ergebnis wird berechnet...</p>
            </div>
          )}
        </section>
      </div>
      
      {/* FAQ-Bereich mit Toggle-Button */}
      <div className="mt-6 border-t border-orange-100 pt-4">
        <button 
          onClick={() => setShowFAQ(!showFAQ)}
          className="flex items-center justify-between w-full text-left px-4 py-2 bg-orange-50 hover:bg-orange-100 rounded-md text-orange-800 transition-colors"
          aria-label={`FAQ-Bereich ${showFAQ ? 'ausblenden' : 'anzeigen'}`}
        >
          <span className="flex items-center font-medium">
            <svg className="w-4 h-4 mr-2" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Häufig gestellte Fragen zur MVS-Berechnung
          </span>
          <svg className={`w-5 h-5 transition-transform ${showFAQ ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        
        {showFAQ && (
          <div className="mt-4 bg-white p-4 border border-orange-100 rounded-md shadow-sm">
            <Suspense fallback={<div className="p-4 text-center">FAQ wird geladen...</div>}>
              <LazyFAQSection />
            </Suspense>
          </div>
        )}
      </div>
    </section>
  )
}
